import { createSignal, onMount, createMemo, onCleanup, Show } from 'solid-js';
import { useParams, useNavigate } from '@solidjs/router';
import { useApi, isOk } from '../api';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import Text from '../components/Text';
import { EmptyState } from '../components/EmptyState';
import { ProfileInfoSnapshot } from '../models/ProfileInfo';
import { Citybuild } from '../models/Citybuild';
import styles from './BotProfileHistory.module.css';

import {
    Chart,
    CategoryScale,
    TimeScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    Title,
    Tooltip,
    Legend,
    BarElement
} from 'chart.js';
import 'chartjs-adapter-moment';

Chart.register(
    CategoryScale, TimeScale, LinearScale, PointElement, LineElement, LineController,
    Title, Tooltip, Legend, BarElement
);

type MetricKey = 'cash' | 'bankBalance' | 'orbs' | 'kristalle' | 'hoursPlayed' | 'players';

interface ChartSpec {
    key: MetricKey;
    label: string;
    color: string;
    format: (v: number) => string;
    get: (s: ProfileInfoSnapshot) => number | null;
}

function formatNumber(n: number): string {
    return new Intl.NumberFormat('de-DE').format(n);
}

function formatCurrency(n: number): string {
    return formatNumber(n) + '$';
}

const CHART_SPECS: ChartSpec[] = [
    { key: 'cash', label: 'STAT_CASH', color: '#60a5fa', format: formatCurrency, get: s => s.cash },
    { key: 'bankBalance', label: 'STAT_BANK', color: '#7c4dff', format: formatCurrency, get: s => s.bankBalance },
    { key: 'orbs', label: 'STAT_ORBS', color: '#ffb74d', format: formatNumber, get: s => s.orbs },
    { key: 'kristalle', label: 'STAT_KRISTALLE', color: '#4dd0e1', format: formatNumber, get: s => s.kristalle },
    { key: 'hoursPlayed', label: 'STAT_HOURS_PLAYED', color: '#4caf50', format: v => v.toFixed(1) + ' h', get: s => s.hoursPlayed },
    { key: 'players', label: 'STAT_PLAYERS_ONLINE', color: '#ef5350', format: formatNumber, get: s => s.currentPlayers },
];

export default function BotProfileHistory() {
    const params = useParams();
    const navigate = useNavigate();
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();

    const [snapshots, setSnapshots] = createSignal<ProfileInfoSnapshot[]>([]);
    const [loading, setLoading] = createSignal(true);
    const [fromDate, setFromDate] = createSignal('');
    const [toDate, setToDate] = createSignal('');
    const canvasRefs = new Map<MetricKey, HTMLCanvasElement>();
    const chartRefs = new Map<MetricKey, any>();

    const botId = () => params.id || '';

    onMount(async () => {
        const today = new Date();
        const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        setToDate(today.toISOString().split('T')[0]);
        setFromDate(sevenDaysAgo.toISOString().split('T')[0]);
        await fetchHistory();
    });

    async function fetchHistory() {
        if (!botId()) return;
        try {
            setLoading(true);
            const res = await api.profileInfo.getHistory(botId(), {
                ...(fromDate() ? { from: new Date(`${fromDate()}T00:00:00`).toISOString() } : {}),
                ...(toDate() ? { to: new Date(`${toDate()}T23:59:59.999`).toISOString() } : {}),
            });
            if (isOk(res.data)) {
                setSnapshots((res.data.value.snapshots ?? []) as unknown as ProfileInfoSnapshot[]);
            } else {
                toast?.error(t('BOT_PROFILE_HISTORY_LOAD_FAILED'));
            }
        } finally {
            setLoading(false);
        }
    }

    const latest = createMemo(() => {
        const all = snapshots();
        return all.length > 0 ? all[all.length - 1] : null;
    });

    onCleanup(() => {
        for (const chart of chartRefs.values()) chart.destroy();
        chartRefs.clear();
    });

    function mountCanvas(spec: ChartSpec, el: HTMLCanvasElement) {
        canvasRefs.set(spec.key, el);
        queueMicrotask(() => {
            if (el.isConnected) renderChart(spec, snapshots());
        });
    }

    function renderChart(spec: ChartSpec, data: ProfileInfoSnapshot[]) {
        const canvas = canvasRefs.get(spec.key);
        if (!canvas || data.length === 0) return;

        const existing = chartRefs.get(spec.key);
        if (existing) existing.destroy();

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const chart = new Chart(ctx, {
            type: 'line',
            data: {
                labels: data.map(s => new Date(s.createdAt).toISOString()),
                datasets: [{
                    label: t(spec.label),
                    data: data.map(s => ({ x: new Date(s.createdAt).toISOString(), y: spec.get(s) })),
                    borderColor: spec.color,
                    backgroundColor: spec.color + '22',
                    borderWidth: 2,
                    fill: true,
                    tension: 0.2,
                    pointRadius: 0,
                    pointHoverRadius: 4,
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: false,
                interaction: { mode: 'nearest', intersect: false },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        backgroundColor: 'rgba(30, 30, 30, 0.95)',
                        titleColor: '#ffffff',
                        bodyColor: '#e0e0e0',
                        borderColor: '#444444',
                        borderWidth: 1,
                        callbacks: {
                            label: (ctx) => spec.format(ctx.parsed.y ?? 0),
                        }
                    }
                },
                scales: {
                    x: {
                        type: 'time',
                        time: { unit: 'day', tooltipFormat: 'MMM DD, HH:mm' },
                        ticks: { color: '#999999' },
                        grid: { color: 'rgba(255, 255, 255, 0.05)' }
                    },
                    y: {
                        ticks: { color: '#999999' },
                        grid: { color: 'rgba(255, 255, 255, 0.05)' }
                    }
                }
            }
        });
        chartRefs.set(spec.key, chart);
    }

    function setRangeDays(days: number) {
        const now = new Date();
        const past = new Date();
        past.setDate(now.getDate() - days);
        setFromDate(past.toISOString().split('T')[0]);
        setToDate(now.toISOString().split('T')[0]);
        fetchHistory();
    }

    return (
        <div class={styles.container}>
            <button class={styles.backBtn} onClick={() => navigate('/botengine')}>
                <i class="fa-solid fa-arrow-left" />
                <Text val="BOT_PROFILE_HISTORY_BACK" />
            </button>

            <div class={styles.header}>
                <h1 class={styles.title}><Text val="BOT_PROFILE_HISTORY_TITLE" /></h1>
                <p class={styles.subtitle}>
                    <Text val="BOT_PROFILE_HISTORY_SUBTITLE" />
                </p>
            </div>

            <Show
                when={!loading()}
                fallback={
                    <div class={styles.loading}>
                        <i class="fa-solid fa-spinner fa-spin" />
                        <span><Text val="BOT_PROFILE_HISTORY_LOADING" /></span>
                    </div>
                }
            >
                <Show
                    when={snapshots().length > 0}
                    fallback={
                        <EmptyState
                            icon="fa-inbox"
                            title={t('BOT_PROFILE_HISTORY_EMPTY_TITLE')}
                            hint={t('BOT_PROFILE_HISTORY_EMPTY_HINT')}
                        />
                    }
                >
                    <Show when={latest()}>
                        <div class={styles.statsGrid}>
                            <div class={styles.statCard}>
                                <div class={styles.statCardValue}>{formatCurrency(latest()!.cash)}</div>
                                <div class={styles.statCardLabel}><Text val="STAT_CASH" /></div>
                            </div>
                            <div class={styles.statCard}>
                                <div class={styles.statCardValue}>{formatCurrency(latest()!.bankBalance)}</div>
                                <div class={styles.statCardLabel}><Text val="STAT_BANK" /></div>
                            </div>
                            <div class={styles.statCard}>
                                <div class={styles.statCardValue}>{latest()!.orbs != null ? formatNumber(latest()!.orbs!) : t('ORBS_UNKNOWN')}</div>
                                <div class={styles.statCardLabel}><Text val="STAT_ORBS" /></div>
                            </div>
                            <div class={styles.statCard}>
                                <div class={styles.statCardValue}>{latest()!.kristalle != null ? formatNumber(latest()!.kristalle!) : t('KRISTALLE_UNKNOWN')}</div>
                                <div class={styles.statCardLabel}><Text val="STAT_KRISTALLE" /></div>
                            </div>
                            <div class={styles.statCard}>
                                <div class={styles.statCardValue}>{latest()!.hoursPlayed.toFixed(1)}</div>
                                <div class={styles.statCardLabel}><Text val="STAT_HOURS_PLAYED" /></div>
                            </div>
                            <div class={styles.statCard}>
                                <div class={styles.statCardValue}>{Citybuild[latest()!.citybuild] ?? latest()!.citybuild}</div>
                                <div class={styles.statCardLabel}><Text val="CITYBUILD" /></div>
                            </div>
                            <div class={styles.statCard}>
                                <div class={styles.statCardValue}>{latest()!.currentPlayers}/{latest()!.maxPlayers}</div>
                                <div class={styles.statCardLabel}><Text val="STAT_ONLINE" /></div>
                            </div>
                        </div>
                    </Show>

                    <div class={styles.controls}>
                        <div class={styles.dateRange}>
                            <input
                                type="date"
                                class={styles.dateInput}
                                value={fromDate()}
                                onChange={e => setFromDate(e.currentTarget.value)}
                            />
                            <span class={styles.dateSeparator}>-</span>
                            <input
                                type="date"
                                class={styles.dateInput}
                                value={toDate()}
                                onChange={e => setToDate(e.currentTarget.value)}
                            />
                            <button class="btn btn-primary btn-sm" onClick={fetchHistory}><Text val="BOT_PROFILE_HISTORY_APPLY" /></button>
                        </div>
                        <div class={styles.quickRanges}>
                            <button class="btn btn-secondary btn-sm" onClick={() => setRangeDays(1)}>24h</button>
                            <button class="btn btn-secondary btn-sm" onClick={() => setRangeDays(7)}>7d</button>
                            <button class="btn btn-secondary btn-sm" onClick={() => setRangeDays(30)}>30d</button>
                            <button class="btn btn-secondary btn-sm" onClick={() => setRangeDays(90)}>90d</button>
                        </div>
                    </div>

                    {CHART_SPECS.map(spec => (
                        <div class={styles.chartSection}>
                            <h3 class={styles.chartTitle}>{t(spec.label)}</h3>
                            <div class={styles.chartCanvasWrap}>
                                <canvas ref={el => mountCanvas(spec, el)} />
                            </div>
                        </div>
                    ))}
                </Show>
            </Show>
        </div>
    );
}
